import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Download, Loader2, Printer } from 'lucide-react';
import StudentIDCardTemplate from '../id-card/StudentIDCardTemplate';
import api, { getProfileAssetUrl } from '../../utils/api';
import { downloadIdCardPdf, printIdCard } from '../../utils/idCardExportService';
import { idCardImageCacheKey } from '../../utils/idCardCardData';
import { useToast } from '../../hooks/use-toast';

export default function IdCardPreviewModal({
  open,
  onOpenChange,
  student,
  schoolId,
  format = 'portrait',
  design = 'saffron',
}) {
  const { toast } = useToast();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open || !schoolId) {
      setSchool(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api.get(`schools/${schoolId}`)
      .then((res) => {
        if (!cancelled) setSchool(res.data?.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setSchool(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, schoolId]);

  const photoUrl = student ? getProfileAssetUrl(student.avatar) : '';
  const cacheKey = idCardImageCacheKey(school);

  const handleDownload = async () => {
    if (!student || !school) return;
    setDownloading(true);
    try {
      const fn = await downloadIdCardPdf(student, school, photoUrl, { format, design, imageCacheKey: cacheKey });
      toast({ title: 'Downloaded', description: fn });
    } catch (e) {
      toast({ title: 'Failed', description: e?.message || 'PDF error', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = async () => {
    if (!student || !school) return;
    try {
      await printIdCard(student, school, photoUrl, { format, design, imageCacheKey: cacheKey });
    } catch (e) {
      toast({ title: 'Print failed', description: e?.message || 'Could not print', variant: 'destructive' });
    }
  };

  const studentName = student
    ? [student.first_name, student.last_name].filter(Boolean).join(' ')
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex shrink-0 flex-col gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <DialogTitle>{studentName ? `ID Card — ${studentName}` : 'ID Card Preview'}</DialogTitle>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" disabled={!student || !school || loading} onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />Print
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!student || !school || loading || downloading}
              onClick={handleDownload}
              className="bg-gradient-to-r from-[#FF6B00] to-[#E65100] hover:opacity-95"
            >
              <Download className="h-4 w-4 mr-2" />
              {downloading ? 'Generating…' : 'Download PDF'}
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…
            </div>
          ) : !student || !school ? (
            <div className="flex items-center justify-center py-16 text-red-500">Could not load card data</div>
          ) : (
            <StudentIDCardTemplate
              compact
              format={format}
              design={design}
              student={student}
              school={school}
              photoUrl={photoUrl}
              imageCacheKey={cacheKey}
              previewVersion={cacheKey}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
